import { createPortal } from 'react-dom'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import React, { useState, useEffect, useCallback, useRef } from 'react'

import { Icon } from '@iconify/react'

// antd
import { message, Input, Button, Typography, Divider, Tooltip } from 'antd'

// slices
import {
    getSubTaskListMain,
    getSubTaskListSupport,
    mainTaskCreate,
    mainTaskUpdate,
    taskCreateSupport,
    taskUpdateSupport,
    tasksState,
    updateSubTaskListItem,
} from '../../taskRefactored/tasks.slice'
import {
    buildMainTaskUpdateBodyFromTask,
    isTaskMutationSuccess,
    resolveReferenceId,
} from '../../taskRefactored/tasks.service'

// css
import styles from './ProjectSubTasksSection.module.css'

// components
import DateRangePicker from './DateRangePicker'
import AssigneeSelectorButton from './AssigneeSelectorButton'
import { showActionNotification } from '../../../components/notification'

import unicornFxImage from '../../../assets/images/task/unicorn.png'

const { TextArea } = Input
const { Text } = Typography

const resolveSubTaskAssigneeId = (assigneeId) =>
    resolveReferenceId(assigneeId?.[0] ?? assigneeId)

const ProjectSubTasksSection = ({
    taskId,
    onTaskUpdate,
    filteredAssignees = [],
    projectId,
    getAssigneeColor,
    getInitials,
    onTaskClick,
    triggerCreateChild,
    taskListKind = 'support',
    readOnly = false,
}) => {
    const dispatch = useDispatch()

    const { id } = useParams()

    const { subTasksList, selectedTask } = useSelector(tasksState)

    const [createChild, setCreateChild] = useState(false)
    const subTaskInputRef = useRef(null)
    const subTaskSectionRef = useRef(null)
    const [subTaskLoadingButton, setSubTaskLoadingButton] = useState(false)
    const [titleValue, setTitleValue] = useState('')
    const [editingSubTaskId, setEditingSubTaskId] = useState(null)
    const [editingTitle, setEditingTitle] = useState('')
    const [hoveredSubTaskId, setHoveredSubTaskId] = useState(null)
    const [togglingSubTaskId, setTogglingSubTaskId] = useState(null)
    const [showCompletionCelebration, setShowCompletionCelebration] =
        useState(false)

    const isMainTask = taskListKind === 'main'
    const resolvedProjectId = projectId || id

    const updateSubTask = useCallback(
        async (subTask, overrides = {}) => {
            if (!subTask?._id) {
                return { ok: false, message: 'Subtask not found' }
            }

            let result
            if (isMainTask) {
                const body = {
                    ...buildMainTaskUpdateBodyFromTask(
                        subTask,
                        resolvedProjectId,
                        overrides
                    ),
                    ...(overrides.isCompleted !== undefined
                        ? { isCompleted: overrides.isCompleted }
                        : {}),
                }
                // Subtask updates must not resend parent linkage
                delete body.parent
                delete body.parentId
                result = await dispatch(
                    mainTaskUpdate({ id: subTask._id, body })
                )
            } else {
                const payload = {
                    _id: subTask._id,
                    updateTask: {
                        title:
                            overrides.title ?? (subTask?.title || ''),
                        projectId: resolvedProjectId,
                        type: overrides.type ?? (subTask?.type || 'Task'),
                        assigneeId:
                            overrides.assigneeId !== undefined
                                ? overrides.assigneeId
                                : resolveSubTaskAssigneeId(subTask?.assigneeId),
                        priority:
                            overrides.priority ?? (subTask?.priority || 'Low'),
                        status:
                            overrides.status ??
                            (subTask?.status || 'To Do'),
                        description:
                            overrides.description !== undefined
                                ? overrides.description
                                : subTask?.description || '',
                        start:
                            overrides.start !== undefined
                                ? overrides.start
                                : subTask?.start || null,
                        end:
                            overrides.end !== undefined
                                ? overrides.end
                                : subTask?.end || null,
                        ...(overrides.isCompleted !== undefined
                            ? { isCompleted: overrides.isCompleted }
                            : {}),
                    },
                }
                result = await dispatch(taskUpdateSupport(payload))
            }

            const data = result?.payload?.data
            return {
                ok: isMainTask
                    ? isTaskMutationSuccess(data)
                    : Boolean(data?.success),
                message: data?.message,
            }
        },
        [dispatch, isMainTask, resolvedProjectId]
    )

    const fetchSubTasks = useCallback(async () => {
        if (!taskId) return
        const fetchList = isMainTask
            ? getSubTaskListMain
            : getSubTaskListSupport
        await dispatch(
            fetchList({
                skip: 0,
                limit: 100,
                parentId: taskId,
            })
        )
    }, [dispatch, taskId, isMainTask])

    useEffect(() => {
        if (taskId) {
            fetchSubTasks()
        }
    }, [taskId, fetchSubTasks])

    const createChildTask = async () => {
        if (!titleValue.trim()) {
            return
        }

        setSubTaskLoadingButton(true)
        try {
            const parent = selectedTask?.data
            const parentEpic = resolveReferenceId(
                parent?.epic ?? parent?.epicId
            )
            const parentSprintId = resolveReferenceId(
                parent?.sprintId ?? parent?.sprint
            )
            const payload = {
                title: titleValue.trim(),
                parent: taskId,
                projectId: resolvedProjectId,
                status: parent?.status || 'To Do',
                type: parent?.type || 'Task',
                ...(isMainTask && parentEpic ? { epic: parentEpic } : {}),
                ...(isMainTask && parentSprintId
                    ? { sprintId: parentSprintId }
                    : {}),
            }
            await dispatch(
                isMainTask
                    ? mainTaskCreate(payload)
                    : taskCreateSupport(payload)
            )
            setTitleValue('')
            setCreateChild(false)
            fetchSubTasks()
            onTaskUpdate?.()
            showActionNotification({
                message: 'Subtask created successfully',
                subMessage: titleValue.trim(),
                actionButtonText: 'Undo',
                notificationKey: 'subtask-created',
            })
        } catch (error) {
            message.error('Failed to create subtask')
        } finally {
            setSubTaskLoadingButton(false)
        }
    }

    const handleSubTaskTitleUpdate = async (subTask) => {
        if (!editingTitle.trim()) {
            setEditingSubTaskId(null)
            return
        }

        try {
            const { ok, message: errorMessage } = await updateSubTask(subTask, {
                title: editingTitle.trim(),
            })
            if (ok) {
                setEditingSubTaskId(null)
                setEditingTitle('')
                fetchSubTasks()
                onTaskUpdate?.()
            } else {
                message.error(errorMessage || 'Failed to update subtask')
            }
        } catch (error) {
            message.error('Failed to update subtask')
        }
    }

    const handleDateRangeChange = async (subTask, dateRange) => {
        try {
            const { ok, message: errorMessage } = await updateSubTask(subTask, {
                start: dateRange.start || null,
                end: dateRange.end || null,
            })
            if (ok) {
                fetchSubTasks()
                onTaskUpdate?.()
            } else {
                message.error(
                    errorMessage || 'Failed to update subtask due date'
                )
            }
        } catch (error) {
            message.error('Failed to update subtask due date')
        }
    }

    const handleAssigneeChange = async (subTask, selected) => {
        const previousAssignee = subTask?.assigneeId
        const nextAssignee = selected
            ? {
                  _id: selected._id,
                  name: selected.name,
                  email: selected.email,
                  profilePicture: selected.profilePicture,
              }
            : null

        dispatch(
            updateSubTaskListItem({
                subTaskId: subTask?._id,
                updates: { assigneeId: nextAssignee },
            })
        )

        try {
            const { ok, message: errorMessage } = await updateSubTask(subTask, {
                assigneeId: selected?._id || null,
            })
            if (ok) {
                onTaskUpdate?.()
            } else {
                dispatch(
                    updateSubTaskListItem({
                        subTaskId: subTask?._id,
                        updates: { assigneeId: previousAssignee },
                    })
                )
                message.error(
                    errorMessage || 'Failed to update subtask assignee'
                )
            }
        } catch (error) {
            dispatch(
                updateSubTaskListItem({
                    subTaskId: subTask?._id,
                    updates: { assigneeId: previousAssignee },
                })
            )
            message.error('Failed to update subtask assignee')
        }
    }

    const isCompleted = (subTask) => {
        return (
            subTask?.isCompleted === true ||
            subTask?.status === 'completed' ||
            subTask?.status === 'done'
        )
    }

    const handleToggleSupportSubTaskComplete = async (subTask, e) => {
        e?.stopPropagation?.()
        if (readOnly || !subTask?._id || togglingSubTaskId === subTask?._id)
            return

        const nextCompleted = !isCompleted(subTask)
        setTogglingSubTaskId(subTask?._id)
        try {
            const { ok, message: errorMessage } = await updateSubTask(subTask, {
                isCompleted: nextCompleted,
            })
            if (ok) {
                if (nextCompleted) setShowCompletionCelebration(true)
                fetchSubTasks()
                onTaskUpdate?.()
            } else {
                message.error(
                    errorMessage || 'Failed to update subtask completion'
                )
            }
        } catch (error) {
            message.error('Failed to update subtask completion')
        } finally {
            setTogglingSubTaskId(null)
        }
    }

    // Hide Add subtask button if task has a parent (is already a subtask)
    // or if the parent task type is Bug
    const task = selectedTask?.data
    const hasParentTask = Boolean(
        (Array.isArray(task?.parentTask)
            ? task.parentTask.length > 0
            : task?.parentTask &&
              typeof task.parentTask === 'object' &&
              Object.keys(task.parentTask).length > 0) ||
            resolveReferenceId(task?.parent ?? task?.parentId)
    )
    const canAddSubtasks =
        !readOnly && !/^bug$/i.test(String(task?.type || 'Task').trim())

    // Handle external trigger to create subtask
    useEffect(() => {
        if (triggerCreateChild && !hasParentTask && canAddSubtasks) {
            setCreateChild(true)
            // Scroll to the subtask section after a short delay to ensure it's rendered
            setTimeout(() => {
                if (subTaskSectionRef.current) {
                    subTaskSectionRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                    })
                    // Focus the input after scrolling
                    setTimeout(() => {
                        if (
                            subTaskInputRef.current?.resizableTextArea?.textArea
                        ) {
                            subTaskInputRef.current.resizableTextArea.textArea.focus()
                        } else if (subTaskInputRef.current) {
                            subTaskInputRef.current.focus()
                        }
                    }, 300)
                }
            }, 100)
        }
    }, [triggerCreateChild, hasParentTask, canAddSubtasks])

    useEffect(() => {
        if (!canAddSubtasks && createChild) {
            setCreateChild(false)
        }
    }, [canAddSubtasks, createChild])

    return (
        <div ref={subTaskSectionRef} className={styles.subTasksSection}>
            {(subTasksList?.length > 0 || (createChild && canAddSubtasks)) && (
                <div className={styles.subTasksHeader}>
                    <Text className={styles.subTasksTitle}>Subtasks</Text>
                    <Divider className={styles.subTasksDivider} />
                </div>
            )}

            {(subTasksList?.length > 0 || (createChild && canAddSubtasks)) && (
                <div className={styles.subTasksList}>
                    {subTasksList?.map((subTask) => (
                        <div
                            key={subTask?._id}
                            className={styles.subTaskContainer}
                        >
                            <div
                                key={subTask?._id}
                                className={styles.subTaskItem}
                                style={{
                                    backgroundColor: '#fff',
                                    padding: '2px 0px',
                                }}
                            >
                                <div className={styles.subTaskContent}>
                                    {taskListKind === 'support' && (
                                        <span
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 16,
                                                height: 16,
                                                borderRadius: '50%',
                                                border: `1px solid ${
                                                    isCompleted(subTask)
                                                        ? 'transparent'
                                                        : hoveredSubTaskId ===
                                                            subTask?._id
                                                          ? '#5da182'
                                                          : '#cbd5e1'
                                                }`,
                                                backgroundColor: isCompleted(
                                                    subTask
                                                )
                                                    ? '#5da182'
                                                    : hoveredSubTaskId ===
                                                        subTask?._id
                                                      ? '#f0fdf4'
                                                      : 'transparent',
                                                transition:
                                                    'all 150ms ease, border-color 150ms ease, background-color 150ms ease',
                                                flexShrink: 0,
                                                cursor:
                                                    togglingSubTaskId ===
                                                    subTask?._id
                                                        ? 'not-allowed'
                                                        : 'pointer',
                                                opacity:
                                                    togglingSubTaskId ===
                                                    subTask?._id
                                                        ? 0.6
                                                        : 1,
                                            }}
                                            onMouseEnter={() =>
                                                setHoveredSubTaskId(
                                                    subTask?._id
                                                )
                                            }
                                            onMouseLeave={() =>
                                                setHoveredSubTaskId(null)
                                            }
                                            onClick={(e) =>
                                                handleToggleSupportSubTaskComplete(
                                                    subTask,
                                                    e
                                                )
                                            }
                                        >
                                            <Icon
                                                icon="iconamoon:check-light"
                                                width={11}
                                                height={11}
                                                style={{
                                                    color: isCompleted(subTask)
                                                        ? '#ffffff'
                                                        : hoveredSubTaskId ===
                                                            subTask?._id
                                                          ? '#5da182'
                                                          : '#94a3b8',
                                                    transition:
                                                        'color 150ms ease',
                                                }}
                                            />
                                        </span>
                                    )}
                                    {editingSubTaskId === subTask?._id ? (
                                        <TextArea
                                            value={editingTitle}
                                            onChange={(e) =>
                                                setEditingTitle(e.target.value)
                                            }
                                            onBlur={() =>
                                                handleSubTaskTitleUpdate(
                                                    subTask
                                                )
                                            }
                                            onKeyDown={(e) => {
                                                if (
                                                    e.key === 'Enter' &&
                                                    !e.shiftKey
                                                ) {
                                                    e.preventDefault()
                                                    handleSubTaskTitleUpdate(
                                                        subTask
                                                    )
                                                }
                                            }}
                                            autoFocus
                                            autoSize={{
                                                minRows: 1,
                                                maxRows: 4,
                                            }}
                                            className={styles.subTaskInput}
                                        />
                                    ) : (
                                        <span
                                            className={`${styles.subTaskText} ${
                                                isCompleted(subTask)
                                                    ? styles.completed
                                                    : ''
                                            }`}
                                            title={subTask?.title}
                                            onClick={() => {
                                                if (readOnly) return
                                                setEditingSubTaskId(
                                                    subTask?._id
                                                )
                                                setEditingTitle(subTask?.title)
                                            }}
                                        >
                                            {subTask?.title}
                                        </span>
                                    )}
                                </div>
                                <div className={styles.subTaskActions}>
                                    <div
                                        className={styles.datePickerWrapper}
                                        style={
                                            readOnly
                                                ? { pointerEvents: 'none' }
                                                : undefined
                                        }
                                    >
                                        <DateRangePicker
                                            value={{
                                                start: subTask?.start,
                                                end: subTask?.end,
                                            }}
                                            onChange={
                                                readOnly
                                                    ? undefined
                                                    : (dateRange) =>
                                                          handleDateRangeChange(
                                                              subTask,
                                                              dateRange
                                                          )
                                            }
                                            placeholder=""
                                        />
                                    </div>
                                    <AssigneeSelectorButton
                                        selectedAssignee={subTask?.assigneeId}
                                        onSelect={
                                            readOnly
                                                ? undefined
                                                : (selected) =>
                                                      handleAssigneeChange(
                                                          subTask,
                                                          selected
                                                      )
                                        }
                                        projectId={projectId}
                                        filteredAssignees={filteredAssignees}
                                        getAssigneeColor={getAssigneeColor}
                                        getInitials={getInitials}
                                        taskListKind={taskListKind}
                                    />
                                    <Tooltip title="Subtask details" placement="top">
                                        <button
                                            type="button"
                                            className={styles.arrowIconButton}
                                            aria-label="Subtask details"
                                            onMouseDown={(e) => {
                                                // Prevent blur on title/description so parent
                                                // update+refetch doesn't race the child open.
                                                e.preventDefault()
                                                e.stopPropagation()
                                            }}
                                            onClick={(e) => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                                if (
                                                    onTaskClick &&
                                                    subTask?._id
                                                ) {
                                                    onTaskClick(subTask._id)
                                                }
                                            }}
                                        >
                                            <Icon
                                                icon="mdi:chevron-right"
                                                width={18}
                                                height={18}
                                                className={styles.arrowIcon}
                                            />
                                        </button>
                                    </Tooltip>
                                </div>
                            </div>
                            <Divider className={styles.subTaskDivider} />
                        </div>
                    ))}

                    {createChild && (
                        <div
                            className={styles.subTaskItem}
                            style={{
                                backgroundColor: '#f7f5ff',
                                padding: '2px 16px',
                            }}
                        >
                            <div className={styles.subTaskContent}>
                                {taskListKind === 'support' && (
                                    <Icon
                                        icon="simple-line-icons:check"
                                        width={14}
                                        height={14}
                                        style={{ color: '#94a3b8' }}
                                    />
                                )}
                                <TextArea
                                    value={titleValue}
                                    onChange={(e) =>
                                        setTitleValue(e.target.value)
                                    }
                                    onBlur={createChildTask}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault()
                                            createChildTask()
                                        }
                                    }}
                                    disabled={subTaskLoadingButton}
                                    ref={subTaskInputRef}
                                    autoFocus
                                    autoSize={{ minRows: 1, maxRows: 4 }}
                                    className={styles.subTaskInput}
                                />
                            </div>
                            <div
                                className={`${styles.subTaskActions} ${styles.subTaskCreateActions}`}
                            >
                                <div className={styles.datePickerWrapper}>
                                    <DateRangePicker
                                        value={{ start: null, end: null }}
                                        onChange={() => {}}
                                        placeholder=""
                                    />
                                </div>
                                <AssigneeSelectorButton
                                    selectedAssignee={null}
                                    onSelect={() => {}}
                                    projectId={projectId}
                                    filteredAssignees={filteredAssignees}
                                    getAssigneeColor={getAssigneeColor}
                                    getInitials={getInitials}
                                    taskListKind={taskListKind}
                                />
                                <Icon
                                    icon="mdi:chevron-right"
                                    width={20}
                                    height={20}
                                    className={styles.arrowIcon}
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}

            {!hasParentTask && canAddSubtasks && (
                <div className={styles.subTasksActions}>
                    <Button
                        type="text"
                        icon={
                            <Icon
                                icon="ic:baseline-plus"
                                width={16}
                                height={16}
                            />
                        }
                        onClick={() => setCreateChild(!createChild)}
                        className={styles.addSubTaskButton}
                    >
                        <Text className={styles.addSubTaskText}>
                            Add subtask
                        </Text>
                    </Button>
                </div>
            )}

            {showCompletionCelebration &&
                typeof document !== 'undefined' &&
                createPortal(
                    <div
                        className={styles.taskCompleteUnicornFx}
                        onAnimationEnd={() =>
                            setShowCompletionCelebration(false)
                        }
                    >
                        <img
                            className={styles.taskCompleteUnicornSvg}
                            src={unicornFxImage}
                            alt="Subtask completed celebration"
                            aria-hidden="true"
                        />
                    </div>,
                    document.body
                )}
        </div>
    )
}

export default ProjectSubTasksSection
